import Modifier from 'ember-modifier'
import noUiSlider from 'nouislider'
import type { API, Options, PartialFormatter } from 'nouislider'
import { registerDestructor } from '@ember/destroyable'
import { assert } from '@ember/debug'

type TooltipFormatter = (envodedValue: number) => string | number

interface Signature {
  Args: {
    Named: {
      /**
       * indicates number of handles
       */
      values: number[]
      /**
       * always 1 more than the number of handles
       */
      connect?: boolean[]
      range: {
        min: number
        max: number
      }
      step: number
      tooltips?: boolean | TooltipFormatter | (boolean | TooltipFormatter)[]
      onUpdateAll?: (encodedValues: number[], handleNumber: number) => unknown
      onUpdateValues?: ((encodedValue: number) => unknown)[]
    }
    Positional: unknown[]
  }
  Element: HTMLElement
}

type ElementWithAPI = HTMLElement & { noUiSlider: API }

/**
 * noUiSlider uses floats internally, so sometimes for the unencoded number value we get back a float, and sometimes
 * an integer. Use this function to turn an unencoded value into an integer value representing what would would have
 * been given to the slider to begin with. For example, sometimes this value might be 2.999999998 when it should be 3
 *
 * We use this instead of noUiSliders' 'format' option, because that doesn't provide the formatted value to other
 * noUiSlider formatter hooks like tooltips or pips. The formatted value is only handed to the event callbacks - all
 * other hooks get the unencoded value and need to figure out what it means.
 */
function encodeValue(unencodedValue: number): number {
  return Math.round(unencodedValue)
}

function cleanup(instance: NumberSliderModifier) {
  if (instance.elementWithAPI) {
    instance.elementWithAPI.noUiSlider.off('change')
    instance.elementWithAPI.noUiSlider.destroy()
  }
}

/**
 * This modifier implements the noUiSlider library, attaching it to an element.
 *
 * Usage:
  <div
    {{number-slider
      values=(array 5 20 ...)                 // indicates number of handles
      connect=(array false true false ...)    // always 1 more than the number of handles
      range=(hash min=1 max=50)
      step=1
      tooltips=true
      onUpdateValues=(array this.onFirstValueUpdate this.onSecondValueUpdate ...)
      onUpdateAll=this.onAllValueUpdates
    }}
  />
 *
 * Values, and most common options are updatable. Updating values or some options may animate the change on the slider.
 * Changing other options (eg connect) may require the slider to be recreated to take effect, which is out of the scope
 * of this modifer. noUiSlider will ignore those option changes. Additionally, changing the number of values provided
 * will not changed the number of handles on the slider - that would also require recreating the slider.
 *
 * This modifier has been specifically built to handle integer ranges. This is very flexible - you can use integers and
 * then convert them to the labels you want to show for example. If you have other ranges types you want to cater for,
 * like floats or strings, you may want to consider creating a separate modifier.
 *
 * ---
 *
 * Most args to this modifier match the input in the noUiSlider docs, with some exceptions;
 *
 * tooltips: can be false, true, or a single formatter function. This function will receive an int value representing
 *           the current slider value.
 *
 * onUpdateValues: an array of functions that will be called when a handle has finished changing (the user let go of the
 *                 handle). The function called will be the index of the handle. You probably want to use this arg.
 *
 *                 this.newFrom = (value) => console.log(`from value ${value}`)
 *                 this.newTo = (value) => console.log(`to value ${value}`)
 *                 {{number-slider @onUpdateValues=(array this.newFrom this.newTo)}}
 *
 * onUpdateAll: a function that will be called when ANY handle has finished changing (the user let go of the handle).
 *              The function will receive a values array, and a handle integer representing the handle that was changed.
 *
 *              this.onUpdateAll = (values, handle) => console.log(`handle ${handle} has value ${values[handle]}`)
 *              {{number-slider @onUpdateAll=this.onUpdateAll}}
 */
export default class NumberSliderModifier extends Modifier<Signature> {
  /*
   * If a tooltips arg was provided, build a wrapping function to normalize the values passed to that external formatter
   *
   * Accepts true, false, or a single formatter function. This is a subset of the functionality provided by noUiSlider,
   * which allows for a different function for each handle
   */
  createSliderOptions(named: Signature['Args']['Named']): Options {
    const { connect, range, step, tooltips } = named

    // affects the aria-valuetext attribute on each handle
    const ariaFormat: PartialFormatter = { to: (unencodedValue) => encodeValue(unencodedValue) }

    let newTooltips: Options['tooltips'] = false
    if (tooltips === true) {
      newTooltips = { to: (unencodedValue) => encodeValue(unencodedValue) }
    } else if (typeof tooltips === 'function') {
      newTooltips = { to: (unencodedValue) => tooltips(encodeValue(unencodedValue)) }
    } else if (Array.isArray(tooltips)) {
      newTooltips = tooltips.map((tooltipItem) => {
        if (tooltipItem === true) {
          return { to: (unencodedValue) => encodeValue(unencodedValue) }
        } else if (typeof tooltipItem === 'function') {
          return { to: (unencodedValue) => tooltipItem(encodeValue(unencodedValue)) }
        }
        return false
      })
    }

    return { connect, range, step, ariaFormat, tooltips: newTooltips }
  }

  didSetup = false

  sliderOptions: Nullable<Options> = null

  sliderValues: number[] = []

  elementWithAPI: Nullable<ElementWithAPI> = null

  onUpdateAll: Signature['Args']['Named']['onUpdateAll']

  onUpdateValues: Signature['Args']['Named']['onUpdateValues']

  modify(
    element: Signature['Element'],
    _positional: Signature['Args']['Positional'],
    named: Signature['Args']['Named'],
  ) {
    this.sliderOptions = this.createSliderOptions(named)
    this.sliderValues = named.values ?? []
    this.onUpdateAll = named.onUpdateAll
    this.onUpdateValues = named.onUpdateValues
    if (!this.didSetup) {
      // slider requires a `start` arg for initial values, we can just use the values arg for this
      noUiSlider.create(element, { ...this.sliderOptions, start: this.sliderValues })
      assert('element requires noUiSlider API property', (element as ElementWithAPI)?.noUiSlider)
      this.elementWithAPI = element as ElementWithAPI

      // listen to value changes
      // 'change' event will only call the listener when the user is done sliding (not while in progress)
      this.elementWithAPI.noUiSlider.on('change', this.onChange)

      registerDestructor(this, cleanup)

      this.didSetup = true
    } else {
      // update options
      this.elementWithAPI?.noUiSlider.updateOptions(this.sliderOptions, false)
      // update values
      this.elementWithAPI?.noUiSlider.set(this.sliderValues, false, false)
    }
  }

  /**
   * onChange is called when the user interacts with the slider and new values are generated. This function will call
   * the provided update actions in order to inform the consumer of the new values (actions-up)
   */
  onChange = (_formattedValues: (number | string)[], handleNumber: number, unencodedValues: number[]) => {
    const { onUpdateAll, onUpdateValues } = this

    // See encodeValue() function below for more info
    const values = unencodedValues.map(encodeValue)
    const valueForHandle = values[handleNumber]

    // call action that wants to be notified of _any_ handle value change
    onUpdateAll?.(values, handleNumber)
    // call action for the specific handle that caused the update
    onUpdateValues?.[handleNumber]?.(valueForHandle)
  }
}
